import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * Badge — etykieta / chip stanu.
 *
 * Dwie osie kolorystyczne (rozłączne):
 *  - `color` — historyczna paleta (gray/accent/red/green/amber/blue), domyślna;
 *    NIE zmieniać — pod nią żyje ~186 call-site'ów.
 *  - `tone` — paleta statusów (neutral/success/warning/danger/info/purple/accent)
 *    przejęta z dawnego `ui/StatusBadge`. Podanie `tone` wygrywa nad `color`.
 *
 * Trzecia oś — `style` (Figma „08.3 · Selection & status"): subtle (domyślny,
 * pixel-parytet z poprzednią wersją) / solid (wypełnione, biały tekst) / outline
 * (ramka + przezroczyste tło). Solid/outline nadpisują kolory `tone`/`color` przez
 * `compoundVariants` — `cn()` na końcu rozwiązuje konflikt przez `tailwind-merge`
 * (ostatnia klasa w stringu wygrywa), więc domyślny render (`style` pominięty)
 * pozostaje BAJT W BAJT taki sam jak przed zmianą.
 *
 *   <Badge color="red">3</Badge>
 *   <Badge tone="success">Zatwierdzony</Badge>
 *   <Badge tone={statusTone(status)} icon={Clock}>{label}</Badge>
 *   <Badge dotColor={typ.color}>{typ.name}</Badge>
 *   <Badge tone="danger" style="solid">Odrzucony</Badge>
 *   <Badge color="accent" style="outline">Wersja robocza</Badge>
 */

const badgeVariants = cva('inline-flex items-center rounded-lg font-medium', {
  variants: {
    // Historyczna paleta — pixel-parytet z poprzednią wersją Badge.
    color: {
      gray: 'bg-bg-tertiary text-text-secondary',
      accent: 'bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-accent-200',
      red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      amber: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
    // Paleta statusów (dawny StatusBadge).
    tone: {
      neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      accent: 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400',
      success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      danger: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    },
    size: {
      sm: 'px-1.5 py-0.5 text-[10px] gap-1',
      md: 'px-2 py-0.5 text-xs gap-1',
      lg: 'px-3 py-1 text-sm gap-1.5',
    },
    // Nowa oś (dodatek, nie zmienia domyślnego renderu — patrz komentarz u góry).
    style: {
      subtle: '',
      solid: 'border border-transparent',
      outline: 'border bg-transparent',
    },
  },
  defaultVariants: { size: 'md', style: 'subtle' },
  compoundVariants: [
    // --- tone × style ---
    { tone: 'neutral', style: 'solid', class: 'bg-gray-600 text-white dark:bg-gray-500' },
    {
      tone: 'neutral',
      style: 'outline',
      class: 'border-gray-400 text-gray-600 dark:border-gray-500 dark:text-gray-300',
    },
    { tone: 'accent', style: 'solid', class: 'bg-accent-600 text-white' },
    { tone: 'accent', style: 'outline', class: 'border-accent-600 text-accent-600' },
    {
      tone: 'success',
      style: 'solid',
      class: 'bg-[rgb(var(--color-success))] text-white',
    },
    {
      tone: 'success',
      style: 'outline',
      class: 'border-[rgb(var(--color-success))] text-[rgb(var(--color-success))]',
    },
    { tone: 'warning', style: 'solid', class: 'bg-amber-600 text-white' },
    {
      tone: 'warning',
      style: 'outline',
      class: 'border-amber-500 text-amber-700 dark:border-amber-400 dark:text-amber-400',
    },
    {
      tone: 'danger',
      style: 'solid',
      class: 'bg-[rgb(var(--color-error))] text-white',
    },
    {
      tone: 'danger',
      style: 'outline',
      class: 'border-[rgb(var(--color-error))] text-[rgb(var(--color-error))]',
    },
    { tone: 'info', style: 'solid', class: 'bg-blue-600 text-white' },
    {
      tone: 'info',
      style: 'outline',
      class: 'border-blue-500 text-blue-700 dark:border-blue-400 dark:text-blue-400',
    },
    { tone: 'purple', style: 'solid', class: 'bg-purple-600 text-white' },
    {
      tone: 'purple',
      style: 'outline',
      class: 'border-purple-500 text-purple-700 dark:border-purple-400 dark:text-purple-400',
    },
    // --- color × style (historyczna paleta) ---
    { color: 'gray', style: 'solid', class: 'bg-gray-600 text-white dark:bg-gray-500' },
    {
      color: 'gray',
      style: 'outline',
      class: 'border-gray-400 text-gray-600 dark:border-gray-500 dark:text-gray-300',
    },
    { color: 'accent', style: 'solid', class: 'bg-accent-600 text-white' },
    { color: 'accent', style: 'outline', class: 'border-accent-600 text-accent-600' },
    {
      color: 'red',
      style: 'solid',
      class: 'bg-[rgb(var(--color-error))] text-white',
    },
    {
      color: 'red',
      style: 'outline',
      class: 'border-[rgb(var(--color-error))] text-[rgb(var(--color-error))]',
    },
    {
      color: 'green',
      style: 'solid',
      class: 'bg-[rgb(var(--color-success))] text-white',
    },
    {
      color: 'green',
      style: 'outline',
      class: 'border-[rgb(var(--color-success))] text-[rgb(var(--color-success))]',
    },
    { color: 'amber', style: 'solid', class: 'bg-amber-600 text-white' },
    {
      color: 'amber',
      style: 'outline',
      class: 'border-amber-500 text-amber-700 dark:border-amber-400 dark:text-amber-400',
    },
    { color: 'blue', style: 'solid', class: 'bg-blue-600 text-white' },
    {
      color: 'blue',
      style: 'outline',
      class: 'border-blue-500 text-blue-700 dark:border-blue-400 dark:text-blue-400',
    },
  ],
});

export type BadgeColor = NonNullable<VariantProps<typeof badgeVariants>['color']>;
export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;
export type BadgeSize = NonNullable<VariantProps<typeof badgeVariants>['size']>;
export type BadgeStyle = NonNullable<VariantProps<typeof badgeVariants>['style']>;

const ICON_SIZE: Record<BadgeSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'style'> {
  /** Historyczna paleta. Ignorowana gdy podano `tone`. */
  color?: BadgeColor;
  /** Paleta statusów (dawny StatusBadge) — wygrywa nad `color`. */
  tone?: BadgeTone;
  size?: BadgeSize;
  /**
   * Wariant wypełnienia (Figma „08.3"). Domyślnie `subtle` = pixel-parytet z
   * poprzednią wersją Badge. UWAGA: podmienia natywny prop `style` (inline CSS) —
   * żaden z ~186 istniejących call-site'ów go nie używał (zweryfikowane grepem).
   */
  style?: BadgeStyle;
  /** Ikona wiodąca (lucide). */
  icon?: LucideIcon;
  /** Hex/CSS color — renderuje wiodącą kropkę w tym kolorze. */
  dotColor?: string;
  children: ReactNode;
}

export function Badge({
  color,
  tone,
  size = 'md',
  style = 'subtle',
  icon: Icon,
  dotColor,
  children,
  className,
  ...rest
}: BadgeProps) {
  // Rozłączne palety: `tone` wygrywa; inaczej `color` (domyślnie gray).
  const palette = tone ? { tone } : { color: color ?? 'gray' };
  return (
    <span className={cn(badgeVariants({ ...palette, size, style }), className)} {...rest}>
      {dotColor && (
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: dotColor }}
          aria-hidden="true"
        />
      )}
      {Icon && <Icon className={ICON_SIZE[size]} />}
      {children}
    </span>
  );
}

/**
 * Mapuje typowe statusy cyklu życia (PL system) na tonację badge'a.
 * Pokrywa nieobecności, grafiki, delegacje, akceptacje, dokumenty.
 */
export function statusTone(status: string | null | undefined): BadgeTone {
  switch ((status || '').toLowerCase()) {
    case 'approved':
    case 'zatwierdzony':
    case 'zatwierdzone':
    case 'completed':
    case 'zakonczony':
    case 'signed':
    case 'paid':
    case 'oplacony':
    case 'active':
    case 'aktywny':
    case 'finance_approved':
    case 'achieved':
    case 'finalized':
    case 'applied':
    case 'zastosowany':
    case 'accepted':
    case 'zaakceptowany':
      return 'success';
    case 'pending':
    case 'oczekujacy':
    case 'oczekuje':
    case 'submitted':
    case 'zlozony':
    case 'nowy':
    case 'pending_signature':
    case 'pending_approval':
    case 'manager_approved':
    case 'awaiting':
    case 'expiring_soon':
    case 'at_risk':
    case 'review_phase':
    case 'settlement_pending':
    case 'rozliczenie':
      return 'warning';
    case 'planned':
    case 'zaplanowany':
    case 'enrolled':
    case 'attended':
    case 'open_for_enrollment':
    case 'acknowledged':
    case 'pending_l2':
    case 'escalated':
    case 'eskalowany':
    case 'returned':
    case 'zwrocony':
    case 'in_progress':
    case 'w_toku':
      return 'info';
    case 'rejected':
    case 'odrzucony':
    case 'odrzucone':
    case 'failed':
    case 'niezdany':
    case 'declined':
    case 'expired':
    case 'wygasly':
    case 'overdue':
    case 'missed':
      return 'danger';
    case 'cancelled':
    case 'anulowany':
    case 'recalled':
    case 'wycofany':
    case 'skipped':
    case 'archived':
    case 'closed':
    case 'draft':
    case 'szkic':
      return 'neutral';
    default:
      return 'neutral';
  }
}
